import React, { useState, useEffect } from 'react';
import {
  Search, Building2, AlertTriangle, CheckCircle,
  Clock, Eye, Settings, Bell, RefreshCw, ChevronDown, ChevronUp,
  Shield, Activity
} from 'lucide-react';
import api from '../../services/api';

const SERVICE_TIERS = {
  BASIC: {
    name: 'Basic Monitoring',
    color: 'gray',
    description: 'Quarterly sanctions, annual registry checks',
    price: '£25/month'
  },
  STANDARD: {
    name: 'Standard Monitoring',
    color: 'blue',
    description: 'Monthly sanctions, 6-month registry/PSC checks, share monitoring',
    price: '£75/month'
  },
  PREMIUM: {
    name: 'Premium Monitoring',
    color: 'purple',
    description: 'Weekly checks, adverse media, filings monitoring',
    price: '£150/month'
  },
  ENTERPRISE: {
    name: 'Enterprise Monitoring',
    color: 'orange',
    description: 'Daily/real-time monitoring, all sources, dedicated support',
    price: '£300/month'
  }
};

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-green-100 text-green-800 border-green-200'
};

const STATUS_COLORS = {
  NEW: 'bg-blue-100 text-blue-800',
  REVIEWING: 'bg-yellow-100 text-yellow-800',
  REVIEWED: 'bg-green-100 text-green-800',
  DISMISSED: 'bg-gray-100 text-gray-800',
  ACTIONED: 'bg-purple-100 text-purple-800',
  ESCALATED: 'bg-red-100 text-red-800'
};

const Monitoring = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptions, setSubscriptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedTier, setExpandedTier] = useState(null);
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    totalEvents: 0,
    criticalEvents: 0,
    pendingReview: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subsRes, eventsRes, dashboardRes] = await Promise.all([
        api.get('/monitoring/subscriptions').catch(() => ({ data: { subscriptions: [] } })),
        api.get('/monitoring/events?limit=50').catch(() => ({ data: { events: [] } })),
        api.get('/monitoring/dashboard').catch(() => ({ data: {} }))
      ]);

      setSubscriptions(subsRes.data.subscriptions || []);
      setEvents(eventsRes.data.events || []);
      
      if (dashboardRes.data) {
        setStats({
          totalSubscriptions: dashboardRes.data.subscriptions?.total || 0,
          activeSubscriptions: dashboardRes.data.subscriptions?.active || 0,
          totalEvents: dashboardRes.data.events?.total || 0,
          criticalEvents: dashboardRes.data.events?.bySeverity?.CRITICAL || 0,
          pendingReview: dashboardRes.data.events?.byStatus?.NEW || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'subscriptions', label: 'Subscriptions', icon: Building2 },
    { id: 'events', label: 'Events & Alerts', icon: Bell },
    { id: 'tiers', label: 'Service Tiers', icon: Settings }
  ];

  const filteredEvents = events.filter(event => {
    if (filterSeverity && event.severity !== filterSeverity) return false;
    if (filterStatus && event.status !== filterStatus) return false;
    if (searchTerm && !event.title?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getTierColor = (tier) => {
    const colors = {
      BASIC: 'bg-gray-100 text-gray-800 border-gray-300',
      STANDARD: 'bg-blue-100 text-blue-800 border-blue-300',
      PREMIUM: 'bg-purple-100 text-purple-800 border-purple-300',
      ENTERPRISE: 'bg-orange-100 text-orange-800 border-orange-300'
    };
    return colors[tier] || colors.BASIC;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring Services</h1>
          <p className="text-gray-600">Manage ongoing KYB/KYC monitoring subscriptions and alerts</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSubscriptions}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSubscriptions}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Bell className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-2xl font-bold text-gray-900">{stats.criticalEvents}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingReview}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Service Tiers Overview */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Monitoring Service Tiers</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Object.entries(SERVICE_TIERS).map(([key, tier]) => (
                <div key={key} className={`rounded-lg border-2 p-4 ${getTierColor(key)}`}>
                  <h3 className="font-semibold">{tier.name}</h3>
                  <p className="text-sm mt-1 opacity-80">{tier.description}</p>
                  <p className="text-lg font-bold mt-2">{tier.price}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Events</h2>
              <button
                onClick={() => setActiveTab('events')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            {events.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No monitoring events yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 5).map((event) => (
                  <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${SEVERITY_COLORS[event.severity]}`}>
                        {event.severity}
                      </span>
                      <span className="font-medium text-gray-900">{event.title}</span>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(event.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search subscriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <h3 className="text-lg font-medium text-gray-900">No Subscriptions</h3>
              <p className="text-gray-500 mt-1">No monitoring subscriptions have been created yet</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Review</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sub.entityId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sub.companyNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getTierColor(sub.serviceTier)}`}>
                        {sub.serviceTier}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sub.riskLevel}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${sub.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {sub.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(sub.nextFullReview)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button className="text-blue-600 hover:text-blue-700 mr-3">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-700">
                        <Settings className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'events' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="NEW">New</option>
                <option value="REVIEWING">Reviewing</option>
                <option value="REVIEWED">Reviewed</option>
                <option value="DISMISSED">Dismissed</option>
                <option value="ACTIONED">Actioned</option>
              </select>
            </div>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <h3 className="text-lg font-medium text-gray-900">No Events</h3>
              <p className="text-gray-500 mt-1">No monitoring events match your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredEvents.map((event) => (
                <div key={event.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${SEVERITY_COLORS[event.severity]}`}>
                          {event.severity}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${STATUS_COLORS[event.status]}`}>
                          {event.status}
                        </span>
                        <span className="text-xs text-gray-500">{event.source}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm text-gray-500">{formatDate(event.createdAt)}</p>
                      <div className="mt-2 flex gap-2">
                        <button className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                          Review
                        </button>
                        <button className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'tiers' && (
        <div className="space-y-6">
          {Object.entries(SERVICE_TIERS).map(([key, tier]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div
                className={`p-4 cursor-pointer flex items-center justify-between ${getTierColor(key)}`}
                onClick={() => setExpandedTier(expandedTier === key ? null : key)}
              >
                <div className="flex items-center gap-4">
                  <Shield className="w-6 h-6" />
                  <div>
                    <h3 className="font-semibold text-lg">{tier.name}</h3>
                    <p className="text-sm opacity-80">{tier.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xl font-bold">{tier.price}</span>
                  {expandedTier === key ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>

              {expandedTier === key && (
                <div className="p-6 bg-gray-50 border-t">
                  <h4 className="font-medium text-gray-900 mb-4">Included Checks</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {key === 'BASIC' && (
                      <>
                        <CheckCard name="Sanctions Screening" frequency="Quarterly" automated={true} />
                        <CheckCard name="Registry Status" frequency="Annually" automated={true} />
                      </>
                    )}
                    {key === 'STANDARD' && (
                      <>
                        <CheckCard name="Sanctions Screening" frequency="Monthly" automated={true} />
                        <CheckCard name="Registry Status" frequency="Every 6 Months" automated={true} />
                        <CheckCard name="Share Allotment (SH01)" frequency="Monthly" automated={true} source="Companies House" />
                        <CheckCard name="Control Changes (PSC)" frequency="Monthly" automated={true} source="Companies House" />
                      </>
                    )}
                    {key === 'PREMIUM' && (
                      <>
                        <CheckCard name="Sanctions Screening" frequency="Weekly" automated={true} />
                        <CheckCard name="PSC Changes" frequency="Weekly" automated={true} source="Companies House" />
                        <CheckCard name="Share Allotment" frequency="Weekly" automated={true} source="Companies House" />
                        <CheckCard name="New Filings" frequency="Weekly" automated={true} source="Companies House" />
                        <CheckCard name="Adverse Media" frequency="Weekly" automated={true} />
                        <CheckCard name="Control Changes" frequency="Weekly" automated={true} source="Companies House" />
                      </>
                    )}
                    {key === 'ENTERPRISE' && (
                      <>
                        <CheckCard name="Sanctions Screening" frequency="Daily" automated={true} />
                        <CheckCard name="PSC Changes" frequency="Daily" automated={true} source="Companies House" />
                        <CheckCard name="Share Capital Changes" frequency="Daily" automated={true} source="Companies House" />
                        <CheckCard name="Director Changes" frequency="Daily" automated={true} source="Companies House" />
                        <CheckCard name="All Filings" frequency="Daily" automated={true} source="Companies House" />
                        <CheckCard name="Confirmation Statement" frequency="Weekly" automated={true} source="Companies House" />
                        <CheckCard name="Adverse Media" frequency="Daily" automated={true} />
                        <CheckCard name="PEP Screening" frequency="Daily" automated={true} />
                        <CheckCard name="Website Monitoring" frequency="Daily" automated={false} />
                      </>
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">UK Companies House Filings Monitored</h4>
                    <div className="flex flex-wrap gap-2">
                      {key === 'STANDARD' && ['SH01', 'PSC01-09'].map(f => (
                        <span key={f} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono">{f}</span>
                      ))}
                      {key === 'PREMIUM' && ['SH01', 'PSC01-09', 'AP01-04', 'TM01-02'].map(f => (
                        <span key={f} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-mono">{f}</span>
                      ))}
                      {key === 'ENTERPRISE' && ['SH01-08', 'PSC01-09', 'AP01-04', 'TM01-02', 'CH01-04', 'CS01', 'DS01'].map(f => (
                        <span key={f} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-mono">{f}</span>
                      ))}
                      {key === 'BASIC' && (
                        <span className="text-sm text-gray-500">No Companies House filing monitoring included</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper component for check cards
const CheckCard = ({ name, frequency, automated, source }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3">
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium text-gray-900 text-sm">{name}</span>
      {automated ? (
        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Auto</span>
      ) : (
        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Manual</span>
      )}
    </div>
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <Clock className="w-3 h-3" />
      <span>{frequency}</span>
    </div>
    {source && (
      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
        <Shield className="w-3 h-3" />
        <span>{source}</span>
      </div>
    )}
  </div>
);

export default Monitoring;
